// src/components/AIAssistant.jsx
//
// SideMenu's new "AI Assistant" nav item -- the AI Cost Estimation
// module's MVP. Per what we agreed before building this: no LLM, no
// external API key, no per-request cost. This searches your EXISTING
// Rate_Card data (via app.py's /ai/suggest-rate and /ai/similar-items,
// the ai_module_api blueprint) using the same Class -> Asset Group ->
// Asset Element -> Main Element hierarchy the rest of the app already
// navigates, then computes a suggested rate from whatever matched --
// median of matching rates, a confidence label based on sample size and
// spread, and the actual matching rows as evidence. Nothing here writes
// to the database; every result is something you review and decide
// whether to use, same as every other read-only report in this app.
//
// Scope note: this covers 2 of the 6 AI Actions originally proposed
// (Suggest Rate, Suggest Similar Items) plus the AI Input/AI Result
// layout -- Generate Cost Estimate / Explain Rate / Check Estimate /
// Find Missing Items and the free-text "Ask AI" box were deferred to
// build on top of this once it's confirmed working against your real
// data (they'd need a genuine LLM to write "why is this rate higher"-
// style prose, which is a separate decision -- see the E-Mail
// Configuration page's own "no fake buttons" precedent for why this
// isn't stubbed in now).
import React, { useEffect, useState } from "react";
import { api as axios } from "../config/apiConfig";
import { API_ORIGIN } from "../config/apiConfig";

const API_BASE = API_ORIGIN;

function formatCurrency(n) {
  if (n === null || n === undefined || Number.isNaN(Number(n))) return "—";
  return Number(n).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

function confidenceBadgeClass(level) {
  if (level === "High") return "bg-success-subtle text-success";
  if (level === "Medium") return "bg-warning-subtle text-warning";
  return "bg-danger-subtle text-danger";
}

// TEMP: this page was showing a blank white screen for the user in
// production -- something in AIAssistantImpl below throws at runtime,
// but the exact error hasn't been diagnosed yet (would need the
// browser console output). Per request, swapping the real page out for
// this placeholder for now rather than shipping a blank screen.
// AIAssistantImpl and everything it depends on is untouched below --
// once the console error is known and fixed, restore this by renaming
// AIAssistantImpl back to AIAssistant and deleting this wrapper (and
// the "export default" above AIAssistantImpl).
export default function AIAssistant() {
  return (
    <div className="card border-0 shadow-sm">
      <div className="card-body text-center py-5">
        <i className="ti ti-rocket fs-1 text-muted d-block mb-3" />
        <h5 className="text-muted mb-0">This Page is Part of next release</h5>
      </div>
    </div>
  );
}

function AIAssistantImpl() {
  // ---- AI Input: cascading Class -> Asset Group -> Asset Element ->
  // Main Element -> Sub Element, same endpoints/pattern as RateCard.jsx.
  const [classes, setClasses] = useState([]);
  const [assetGroups, setAssetGroups] = useState([]);
  const [assetElements, setAssetElements] = useState([]);
  const [mainElements, setMainElements] = useState([]);
  const [subElementOptions, setSubElementOptions] = useState([]);
  const [unitOptions, setUnitOptions] = useState([]);
  const [boqDivisions, setBoqDivisions] = useState([]);

  const [cId, setCId] = useState("");
  const [agId, setAgId] = useState("");
  const [aeId, setAeId] = useState("");
  const [meId, setMeId] = useState("");
  const [seId, setSeId] = useState("");
  const [boqDivision, setBoqDivision] = useState("");
  const [project, setProject] = useState("");
  const [description, setDescription] = useState("");
  const [unit, setUnit] = useState("");
  const [quantity, setQuantity] = useState("");

  useEffect(() => {
    axios.get(`${API_BASE}/class`).then((res) => setClasses(res.data?.data || [])).catch(() => setClasses([]));
    axios.get(`${API_BASE}/get_units_rate`).then((res) => setUnitOptions(res.data?.data || [])).catch(() => setUnitOptions([]));
    axios.get(`${API_BASE}/boq-division`).then((res) => setBoqDivisions(res.data?.data || [])).catch(() => setBoqDivisions([]));
  }, []);

  useEffect(() => {
    if (!cId) { setAssetGroups([]); return; }
    axios.get(`${API_BASE}/get_asset_groups_rate?c_id=${encodeURIComponent(cId)}`)
      .then((res) => setAssetGroups(res.data?.data || []))
      .catch(() => setAssetGroups([]));
  }, [cId]);

  useEffect(() => {
    if (!cId || !agId) { setAssetElements([]); return; }
    axios.get(`${API_BASE}/get_asset_elements_rate?c_id=${encodeURIComponent(cId)}&ag_id=${encodeURIComponent(agId)}`)
      .then((res) => setAssetElements(res.data?.data || []))
      .catch(() => setAssetElements([]));
  }, [cId, agId]);

  useEffect(() => {
    if (!cId || !agId || !aeId) { setMainElements([]); return; }
    axios.get(`${API_BASE}/get_main_elements_rate?c_id=${encodeURIComponent(cId)}&ag_id=${encodeURIComponent(agId)}&ae_id=${encodeURIComponent(aeId)}`)
      .then((res) => setMainElements(res.data?.data || []))
      .catch(() => setMainElements([]));
  }, [cId, agId, aeId]);

  useEffect(() => {
    if (!cId || !agId || !aeId || !meId) { setSubElementOptions([]); return; }
    axios.get(`${API_BASE}/get_sub_elements_list_rate?c_id=${encodeURIComponent(cId)}&ag_id=${encodeURIComponent(agId)}&ae_id=${encodeURIComponent(aeId)}&me_id=${encodeURIComponent(meId)}`)
      .then((res) => setSubElementOptions(res.data?.data || []))
      .catch(() => setSubElementOptions([]));
  }, [cId, agId, aeId, meId]);

  const handleClassChange = (e) => {
    setCId(e.target.value);
    setAgId(""); setAeId(""); setMeId(""); setSeId("");
  };
  const handleAgChange = (e) => {
    setAgId(e.target.value);
    setAeId(""); setMeId(""); setSeId("");
  };
  const handleAeChange = (e) => {
    setAeId(e.target.value);
    setMeId(""); setSeId("");
  };
  const handleMeChange = (e) => {
    setMeId(e.target.value);
    setSeId("");
  };
  // Picking a Sub Element autofills Description/Unit from that
  // Rate_Card row -- a real, visible effect of the click, not a
  // decorative option. It isn't itself sent to the backend as a filter
  // (see app.py's ai_module_api comment on why: this endpoint's "SE_ID"
  // is actually a Rate_Card.RC_ID, a different id space than the real
  // Sub_Element master table, so matching on it here would compare the
  // wrong column).
  const handleSeChange = (e) => {
    const val = e.target.value;
    setSeId(val);
    const found = subElementOptions.find((s) => String(s.SE_ID) === val);
    if (found) {
      if (found.Short_Description) setDescription(found.Short_Description);
      if (found.Unit) setUnit(found.Unit);
    }
  };

  // ---- AI Actions / AI Result -----------------------------------------
  const [running, setRunning] = useState(null); // "rate" | "similar" | null
  const [resultError, setResultError] = useState("");
  const [rateResult, setRateResult] = useState(null);
  const [similarResult, setSimilarResult] = useState(null);

  const buildCriteria = () => ({
    C_ID: cId || null,
    AG_ID: agId || null,
    AE_ID: aeId || null,
    ME_ID: meId || null,
    Unit: unit || null,
    Description: description || "",
    BOQ_Division: boqDivision || null,
    Project: project || null,
  });

  const hasAnyCriteria = () => Boolean(cId || agId || aeId || meId || description.trim());

  const handleSuggestRate = async () => {
    setResultError("");
    setRateResult(null);
    if (!hasAnyCriteria()) {
      setResultError("Select at least a Class, Asset Group, Asset Element or Main Element, or enter a Description, to search against.");
      return;
    }
    setRunning("rate");
    try {
      const res = await axios.post(`${API_BASE}/ai/suggest-rate`, {
        ...buildCriteria(),
        Quantity: quantity !== "" ? quantity : null,
      });
      if (!res?.data?.success) {
        setResultError(res?.data?.error || "Failed to suggest a rate.");
      } else {
        setRateResult(res.data.data);
      }
    } catch (err) {
      setResultError(err?.response?.data?.error || "Failed to suggest a rate.");
    } finally {
      setRunning(null);
    }
  };

  const handleSimilarItems = async () => {
    setResultError("");
    setSimilarResult(null);
    if (!hasAnyCriteria()) {
      setResultError("Select at least a Class, Asset Group, Asset Element or Main Element, or enter a Description, to search against.");
      return;
    }
    setRunning("similar");
    try {
      const res = await axios.post(`${API_BASE}/ai/similar-items`, buildCriteria());
      if (!res?.data?.success) {
        setResultError(res?.data?.error || "Failed to find similar items.");
      } else {
        setSimilarResult(res.data.data);
      }
    } catch (err) {
      setResultError(err?.response?.data?.error || "Failed to find similar items.");
    } finally {
      setRunning(null);
    }
  };

  return (
    <>
      <div className="page-title-head d-flex align-items-center mb-3">
        <div className="flex-grow-1">
          <h4 className="page-main-title m-0">AI Cost Estimation</h4>
          <p className="text-muted fs-13 mb-0">
            Searches your existing Rate Card data for a recommended rate and similar historical items. Read-only -- nothing here changes your data.
          </p>
        </div>
      </div>

      {/* ---- AI Input ---- */}
      <div className="card border-0 shadow-sm mb-3">
        <div className="card-header bg-transparent border-light">
          <h5 className="card-title m-0 fw-bold">AI Input</h5>
        </div>
        <div className="card-body p-3 p-md-4">
          <div className="row g-3 mb-3">
            <div className="col-md-4">
              <label className="form-label fs-xs fw-medium mb-1">Project (optional, narrows to that project's history)</label>
              <input className="form-control form-control-sm" type="text" placeholder="e.g. Riyadh Tower Phase 2" value={project} onChange={(e) => setProject(e.target.value)} />
            </div>
            <div className="col-md-4">
              <label className="form-label fs-xs fw-medium mb-1">BOQ Division (optional)</label>
              <select className="form-select form-select-sm" value={boqDivision} onChange={(e) => setBoqDivision(e.target.value)}>
                <option value="">Any</option>
                {boqDivisions.map((b) => (
                  <option key={b.BOQ_Division} value={b.BOQ_Division}>{b.DivisionName || b.BOQ_Division}</option>
                ))}
              </select>
            </div>
            <div className="col-md-4">
              <label className="form-label fs-xs fw-medium mb-1">Class</label>
              <select className="form-select form-select-sm" value={cId} onChange={handleClassChange}>
                <option value="">Select Class</option>
                {classes.map((c) => (
                  <option key={c.C_ID} value={c.C_ID}>{c.Class}</option>
                ))}
              </select>
            </div>
          </div>

          <div className="row g-3 mb-3">
            <div className="col-md-3">
              <label className="form-label fs-xs fw-medium mb-1">Asset Group</label>
              <select className="form-select form-select-sm" value={agId} onChange={handleAgChange} disabled={!cId}>
                <option value="">Select Asset Group</option>
                {assetGroups.map((a) => (
                  <option key={a.AG_ID} value={a.AG_ID}>{a.Asset_Group}</option>
                ))}
              </select>
            </div>
            <div className="col-md-3">
              <label className="form-label fs-xs fw-medium mb-1">Asset Element</label>
              <select className="form-select form-select-sm" value={aeId} onChange={handleAeChange} disabled={!agId}>
                <option value="">Select Asset Element</option>
                {assetElements.map((a) => (
                  <option key={a.AE_ID} value={a.AE_ID}>{a.Asset_Element}</option>
                ))}
              </select>
            </div>
            <div className="col-md-3">
              <label className="form-label fs-xs fw-medium mb-1">Main Element</label>
              <select className="form-select form-select-sm" value={meId} onChange={handleMeChange} disabled={!aeId}>
                <option value="">Select Main Element</option>
                {mainElements.map((m) => (
                  <option key={m.ME_ID} value={m.ME_ID}>{m.Main_Element}</option>
                ))}
              </select>
            </div>
            <div className="col-md-3">
              <label className="form-label fs-xs fw-medium mb-1">Sub Element</label>
              <select className="form-select form-select-sm" value={seId} onChange={handleSeChange} disabled={!meId}>
                <option value="">Select Sub Element</option>
                {subElementOptions.map((s) => (
                  <option key={s.SE_ID} value={s.SE_ID}>{s.Sub_Element}</option>
                ))}
              </select>
              <small className="text-muted">Autofills Description/Unit below when picked.</small>
            </div>
          </div>

          <div className="row g-3">
            <div className="col-md-6">
              <label className="form-label fs-xs fw-medium mb-1">Description</label>
              <input className="form-control form-control-sm" type="text" placeholder="e.g. Supply and installation of ceramic floor tiles" value={description} onChange={(e) => setDescription(e.target.value)} />
            </div>
            <div className="col-md-3">
              <label className="form-label fs-xs fw-medium mb-1">Unit</label>
              <select className="form-select form-select-sm" value={unit} onChange={(e) => setUnit(e.target.value)}>
                <option value="">Any</option>
                {unitOptions.map((u) => (
                  <option key={u} value={u}>{u}</option>
                ))}
              </select>
            </div>
            <div className="col-md-3">
              <label className="form-label fs-xs fw-medium mb-1">Quantity</label>
              <input
                className="form-control form-control-sm"
                type="text"
                placeholder="e.g. 5000"
                value={quantity}
                onChange={(e) => {
                  const v = e.target.value;
                  if (v === "" || /^\d*\.?\d*$/.test(v)) setQuantity(v);
                }}
              />
            </div>
          </div>
        </div>
      </div>

      {/* ---- AI Actions ---- */}
      <div className="card border-0 shadow-sm mb-3">
        <div className="card-body p-3 p-md-4 d-flex flex-wrap gap-2">
          <button type="button" className="btn btn-teal px-4" onClick={handleSuggestRate} disabled={running !== null}>
            {running === "rate" ? "Searching..." : "Suggest Rate"}
          </button>
          <button type="button" className="btn btn-outline-secondary px-4" onClick={handleSimilarItems} disabled={running !== null}>
            {running === "similar" ? "Searching..." : "Suggest Similar Items"}
          </button>
        </div>
      </div>

      {resultError && <div className="alert alert-danger">{resultError}</div>}

      {/* ---- AI Result: Suggest Rate ---- */}
      {rateResult && (
        <div className="card border-0 shadow-sm mb-3">
          <div className="card-header bg-transparent border-light">
            <h5 className="card-title m-0 fw-bold">AI Result -- Suggested Rate</h5>
          </div>
          <div className="card-body p-3 p-md-4">
            {!rateResult.Matched ? (
              <div className="text-muted">{rateResult.Message}</div>
            ) : (
              <>
                <div className="row g-3 mb-4">
                  <div className="col-md-3">
                    <div className="text-muted fs-12">Suggested Rate</div>
                    <div className="fs-4 fw-bold text-teal">
                      {rateResult.SuggestedRate !== null ? `SAR ${formatCurrency(rateResult.SuggestedRate)}` : "—"}
                      {unit ? <span className="fs-13 text-muted">/{unit}</span> : null}
                    </div>
                  </div>
                  <div className="col-md-3">
                    <div className="text-muted fs-12">Quantity</div>
                    <div className="fs-4 fw-bold">{rateResult.Quantity !== null ? Number(rateResult.Quantity).toLocaleString("en-US") : "—"}</div>
                  </div>
                  <div className="col-md-3">
                    <div className="text-muted fs-12">Estimated Cost</div>
                    <div className="fs-4 fw-bold">{rateResult.EstimatedCost !== null ? `SAR ${formatCurrency(rateResult.EstimatedCost)}` : "—"}</div>
                  </div>
                  <div className="col-md-3">
                    <div className="text-muted fs-12">Confidence</div>
                    <div>
                      <span className={`badge px-3 py-2 fs-13 ${confidenceBadgeClass(rateResult.Confidence)}`}>{rateResult.Confidence}</span>
                    </div>
                  </div>
                </div>
                <div className="d-flex flex-wrap gap-3 mb-3 text-muted fs-13">
                  <span>Similar Items: <strong>{rateResult.SimilarItemsCount}</strong></span>
                  <span>Rate Year: <strong>{rateResult.RateYear || "—"}</strong></span>
                </div>

                <div className="table-responsive">
                  <table className="mb-0 table table-centered table-custom table-hover text-nowrap w-100">
                    <thead className="bg-green text-white">
                      <tr>
                        <th className="px-3 py-2 text-light">Description</th>
                        <th className="px-3 py-2 text-light">Unit</th>
                        <th className="px-3 py-2 text-light">Rate</th>
                        <th className="px-3 py-2 text-light">Rate Year</th>
                        <th className="px-3 py-2 text-light">Project</th>
                      </tr>
                    </thead>
                    <tbody>
                      {rateResult.SimilarItems.map((item) => (
                        <tr key={item.RC_ID}>
                          <td className="px-3 border-end">{item.Short_Description}</td>
                          <td className="px-3 border-end">{item.Unit}</td>
                          <td className="px-3 border-end">{item.Rate !== null ? formatCurrency(item.Rate) : "—"}</td>
                          <td className="px-3 border-end">{item.RateYear || "—"}</td>
                          <td className="px-3 border-end">{item.ProjectName || "—"}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </>
            )}
          </div>
        </div>
      )}

      {/* ---- AI Result: Similar Items ---- */}
      {similarResult && (
        <div className="card border-0 shadow-sm mb-3">
          <div className="card-header bg-transparent border-light">
            <h5 className="card-title m-0 fw-bold">AI Result -- Similar Items ({similarResult.SimilarItemsCount})</h5>
          </div>
          <div className="card-body p-3 p-md-4">
            {!similarResult.Matched ? (
              <div className="text-muted">No similar items were found in Rate_Card for these inputs.</div>
            ) : (
              <div className="table-responsive">
                <table className="mb-0 table table-centered table-custom table-hover text-nowrap w-100">
                  <thead className="bg-green text-white">
                    <tr>
                      <th className="px-3 py-2 text-light">Description</th>
                      <th className="px-3 py-2 text-light">Asset Element</th>
                      <th className="px-3 py-2 text-light">Unit</th>
                      <th className="px-3 py-2 text-light">Rate</th>
                      <th className="px-3 py-2 text-light">Rate Year</th>
                      <th className="px-3 py-2 text-light">Project</th>
                    </tr>
                  </thead>
                  <tbody>
                    {similarResult.SimilarItems.map((item) => (
                      <tr key={item.RC_ID}>
                        <td className="px-3 border-end">{item.Short_Description}</td>
                        <td className="px-3 border-end">{item.Asset_Element}</td>
                        <td className="px-3 border-end">{item.Unit}</td>
                        <td className="px-3 border-end">{item.Rate !== null ? formatCurrency(item.Rate) : "—"}</td>
                        <td className="px-3 border-end">{item.RateYear || "—"}</td>
                        <td className="px-3 border-end">{item.ProjectName || "—"}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>
      )}
    </>
  );
}
